import React, { useState, useRef, useEffect } from 'react';

interface Citation {
  content?: string;
  paragraph_range?: string;
  section_id?: string;        // e.g., "1.1.2"
  section_number?: string;    // backward compat
  section_name?: string;      // e.g., "General"
  section_path?: string;      // e.g., "1 > 1.1 > 1.1.2"
  chapter_title?: string;     // e.g., "Chapter 1 – The Tender..."
  heading_text?: string;
  locations?: string[];
  location_ref?: string;
  section_contents?: Record<string, {  // Map of section_id -> section data
    content?: string;
    section_name?: string;
    section_path?: string;
    heading_text?: string;
    chapter_title?: string;
  }>;
}

interface ParagraphTooltipProps {
  /** The paragraph reference text like "¶69–94" or "§1.1.2" */
  paragraphRef: string;
  /** The citations array to find matching content */
  citations?: Citation[];
  /** Direction for RTL support */
  isRtl?: boolean;
}

/**
 * Component that renders paragraph references (like ¶69–94) with a hover tooltip
 * showing the actual content from that section.
 */
export const ParagraphTooltip: React.FC<ParagraphTooltipProps> = ({
  paragraphRef,
  citations = [],
  isRtl = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Find matching citation content based on paragraph range or section reference
  const matchingContent = React.useMemo(() => {
    if (!citations || citations.length === 0) return null;

    // Check if this is a section reference (§1.1.2) or paragraph reference (¶69-94)
    const sectionMatch = paragraphRef.match(/§?([\d.]+)/);
    const paraMatch = paragraphRef.match(/¶?(\d+)(?:[–\-](\d+))?/);
    
    // Try to match by section_id first (for structured docs)
    if (sectionMatch) {
      const sectionId = sectionMatch[1];
      for (const citation of citations) {
        // First check section_contents map (stores content per section)
        if (citation.section_contents) {
          // Direct key match
          if (citation.section_contents[sectionId]) {
            const sectionData = citation.section_contents[sectionId];
            return {
              content: sectionData.content?.substring(0, 500) || '',
              section: sectionData.section_name || sectionData.heading_text || '',
              sectionNumber: sectionId,
              sectionPath: sectionData.section_path || '',
              chapterTitle: sectionData.chapter_title || ''
            };
          }
          // Check for key containing §sectionId (e.g., "§1, \"Section Name\"")
          for (const [key, sectionData] of Object.entries(citation.section_contents)) {
            if (key.includes(`§${sectionId}`) || key.startsWith(sectionId)) {
              return {
                content: sectionData.content?.substring(0, 500) || '',
                section: sectionData.section_name || sectionData.heading_text || '',
                sectionNumber: sectionId,
                sectionPath: sectionData.section_path || '',
                chapterTitle: sectionData.chapter_title || ''
              };
            }
          }
        }
        // Fallback: Check section_id or section_number directly
        if (citation.section_id === sectionId || citation.section_number === sectionId) {
          return {
            content: citation.content?.substring(0, 500) || '',
            section: citation.section_name || citation.heading_text || '',
            sectionNumber: citation.section_id || citation.section_number || '',
            sectionPath: citation.section_path || '',
            chapterTitle: citation.chapter_title || ''
          };
        }
        // Check locations array for section references
        if (citation.locations) {
          for (const loc of citation.locations) {
            if (loc.includes(`§${sectionId}`)) {
              // Try to find content from section_contents using location as key
              if (citation.section_contents && citation.section_contents[loc]) {
                const sectionData = citation.section_contents[loc];
                return {
                  content: sectionData.content?.substring(0, 500) || '',
                  section: sectionData.section_name || sectionData.heading_text || '',
                  sectionNumber: sectionId,
                  sectionPath: sectionData.section_path || '',
                  chapterTitle: sectionData.chapter_title || ''
                };
              }
              // Fallback to citation's content
              return {
                content: citation.content?.substring(0, 500) || '',
                section: citation.section_name || citation.heading_text || '',
                sectionNumber: citation.section_id || citation.section_number || '',
                sectionPath: citation.section_path || '',
                chapterTitle: citation.chapter_title || ''
              };
            }
          }
        }
      }
    }

    // Fall back to paragraph range matching
    if (paraMatch) {
      const startPara = parseInt(paraMatch[1], 10);
      const endPara = paraMatch[2] ? parseInt(paraMatch[2], 10) : startPara;

      // Find citations that match this paragraph range
      for (const citation of citations) {
        // Check locations array for matching paragraph references
        if (citation.locations) {
          for (const loc of citation.locations) {
            if (loc.includes(`¶${startPara}`) || loc.includes(`¶${startPara}-${endPara}`)) {
              return {
                content: citation.content?.substring(0, 500) || '',
                section: citation.section_name || citation.heading_text || '',
                sectionNumber: citation.section_id || citation.section_number || '',
                sectionPath: citation.section_path || '',
                chapterTitle: citation.chapter_title || ''
              };
            }
          }
        }

        // Check paragraph_range directly
        if (citation.paragraph_range) {
          const citMatch = citation.paragraph_range.match(/¶?(\d+)(?:[–\-](\d+))?/);
          if (citMatch) {
            const citStart = parseInt(citMatch[1], 10);
            const citEnd = citMatch[2] ? parseInt(citMatch[2], 10) : citStart;
            
            // Check if ranges overlap
            if (startPara <= citEnd && endPara >= citStart) {
              return {
                content: citation.content?.substring(0, 500) || '',
                section: citation.section_name || citation.heading_text || '',
                sectionNumber: citation.section_id || citation.section_number || '',
                sectionPath: citation.section_path || '',
                chapterTitle: citation.chapter_title || ''
              };
            }
          }
        }
      }
    }

    // If no match found, return first citation with content as fallback
    const firstWithContent = citations.find(c => c.content);
    if (firstWithContent) {
      return {
        content: firstWithContent.content?.substring(0, 500) || '',
        section: firstWithContent.section_name || firstWithContent.heading_text || '',
        sectionNumber: firstWithContent.section_id || firstWithContent.section_number || '',
        sectionPath: firstWithContent.section_path || '',
        chapterTitle: firstWithContent.chapter_title || ''
      };
    }

    return null;
  }, [paragraphRef, citations]);

  // Position tooltip when showing
  useEffect(() => {
    if (showTooltip && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 350;
      
      // Position below the reference, centered
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      
      // Keep tooltip within viewport
      if (left < 10) left = 10;
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }

      setTooltipPosition({
        top: rect.bottom + window.scrollY + 8,
        left: left + window.scrollX
      });
    }
  }, [showTooltip]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        style={{
          color: '#0078d4',
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
          fontWeight: 500,
          padding: '0 2px',
          borderRadius: 3,
          transition: 'background-color 0.15s ease',
          backgroundColor: showTooltip ? 'rgba(0, 120, 212, 0.1)' : 'transparent'
        }}
        title={matchingContent ? 'Click to view content' : 'Paragraph reference'}
      >
        {paragraphRef}
      </span>

      {showTooltip && matchingContent && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            zIndex: 9999,
            maxWidth: 400,
            minWidth: 280,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            padding: 0,
            overflow: 'hidden',
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        >
          {/* Header with section info */}
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '8px 12px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span style={{ fontSize: 16 }}>📄</span>
            <div>
              {matchingContent.sectionNumber && (
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#0078d4',
                  marginRight: isRtl ? 0 : 6,
                  marginLeft: isRtl ? 6 : 0
                }}>
                  Section {matchingContent.sectionNumber}
                </span>
              )}
              {matchingContent.section && (
                <span style={{
                  fontSize: 11,
                  color: '#6b7280',
                  fontWeight: 500
                }}>
                  {matchingContent.section.length > 40 
                    ? matchingContent.section.substring(0, 40) + '...' 
                    : matchingContent.section}
                </span>
              )}
              {!matchingContent.sectionNumber && !matchingContent.section && (
                <span style={{
                  fontSize: 11,
                  color: '#6b7280',
                  fontWeight: 500
                }}>
                  {paragraphRef}
                </span>
              )}
            </div>
          </div>

          {/* Content preview */}
          <div style={{
            padding: '10px 12px',
            fontSize: 12,
            lineHeight: 1.5,
            color: '#374151',
            maxHeight: 200,
            overflowY: 'auto'
          }}>
            {matchingContent.content || 'Content preview not available'}
            {matchingContent.content && matchingContent.content.length >= 500 && (
              <span style={{ color: '#9ca3af' }}>...</span>
            )}
          </div>

          {/* Footer hint */}
          <div style={{
            backgroundColor: '#fafafa',
            padding: '6px 12px',
            borderTop: '1px solid #e5e7eb',
            fontSize: 10,
            color: '#9ca3af',
            textAlign: 'center'
          }}>
            {isRtl ? 'לחץ כדי להשאיר פתוח' : 'Click to keep open'}
          </div>
        </div>
      )}
    </>
  );
};

export default ParagraphTooltip;
